import type { TFunction } from 'i18next';
import { highlight, languages } from 'prismjs/components/prism-core';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'prismjs/components/prism-markup';
import 'prismjs/themes/prism.css';
import Editor from 'react-simple-code-editor';

import { FIELD_FRAME_CLASS } from './templateStyles.ts';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import Table, { TableScrollWrapper } from '../../../core/components/Table.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import type {
    FieldDefinition,
    IterationType,
    MappingSource,
    OutputMapperConfig,
    OutputType,
    FileEncoding,
    LineEnding,
    TemplateFormat,
    TypeDefinition,
} from '../OutputMapperEditor/types.ts';

export type ConfigUpdater = OutputMapperConfig | ((current: OutputMapperConfig | null) => OutputMapperConfig | null);

export const getOutputType = (config: OutputMapperConfig | null): OutputType => config?.output_type ?? 'json';

// Template and XML outputs are one document per record that loops over the line items itself, so they always run
// per record and the iteration setting is hidden for them.
export const usesIteration = (outputType: OutputType): boolean => outputType !== 'template' && outputType !== 'xml';

export const outputTypeOptions = (t: TFunction<'integrations'>): { value: OutputType; label: string }[] => [
    { value: 'json', label: t('outputMapper.settings.outputType.json', 'JSON') },
    { value: 'xml', label: t('outputMapper.settings.outputType.xml', 'Standard XML') },
    { value: 'txt', label: t('outputMapper.settings.outputType.txt', 'Standard TXT') },
    { value: 'csv', label: t('outputMapper.settings.outputType.csv', 'Standard CSV') },
    { value: 'xlsx', label: t('outputMapper.settings.outputType.xlsx', 'Standard Excel') },
    { value: 'template', label: t('outputMapper.settings.outputType.template', 'Template (Custom)') },
];

export const getTemplateFormat = (config: OutputMapperConfig | null): TemplateFormat =>
    config?.template_format ?? 'xml';

export const getIterationType = (config: OutputMapperConfig | null): IterationType => {
    if (config?.iteration_type) return config.iteration_type;
    // Read the legacy LoS `output_level` so stored dev configs keep working until refreshed.
    if (config?.output_level === 'article') return 'article_suggestions';
    return 'record';
};

export const getMappingSource = (config: OutputMapperConfig | null): MappingSource =>
    config?.default_mapping_source ?? 'channel_fields';

export const getXmlSerializerModule = (config: OutputMapperConfig | null): string =>
    config?.xml_serializer_module ?? '';

export const getTxtDelimiter = (config: OutputMapperConfig | null): string => config?.txt_delimiter || ',';

// --- Article-suggestion editor view -----------------------------------------

const withoutPositions = (fields: FieldDefinition[]): FieldDefinition[] =>
    fields.filter((field) => field.name !== 'positions');

const buildArticleSuggestionInputTypes = (inputTypes: TypeDefinition[]): TypeDefinition[] => {
    const positionType = inputTypes.find((type) => type.name === 'Position');
    const headerType = inputTypes.find((type) => type.name === 'Header');
    const articleSetType = inputTypes.find((type) => type.name === 'ArticleSet');
    const articleType = inputTypes.find((type) => type.name === 'Article');

    // Owners without the LoS article shape keep their input types unchanged.
    if (!positionType || !headerType) return inputTypes;

    const result: TypeDefinition[] = [];
    if (articleType) result.push(articleType);
    if (articleSetType) result.push(articleSetType);
    result.push(positionType);
    result.push({ ...headerType, fields: withoutPositions(headerType.fields) });
    return result;
};

/**
 * Transform the config purely for display in the visual editor based on the iteration type.
 * For `article_suggestions` the per-iteration roots (Article/ArticleSet/Position/Header) are surfaced
 * as the primary sources; other iterations show the config unchanged.
 */
export const iterationEditorConfigTransform = (config: OutputMapperConfig): OutputMapperConfig => {
    if (getIterationType(config) !== 'article_suggestions') return config;
    return { ...config, input_types: buildArticleSuggestionInputTypes(config.input_types) };
};

/** Fold a visual-editor change back into the stored config, preserving the full persisted schema. */
export const iterationMergeVisualEditorChange = (
    next: OutputMapperConfig,
    current: OutputMapperConfig | null
): OutputMapperConfig => {
    if (!current) return next;
    return { ...next, input_types: current.input_types };
};

/** Remount the visual editor when the iteration changes so the surfaced roots refresh. */
export const iterationVisualEditorKey = (config: OutputMapperConfig | null): string => getIterationType(config);

// --- Tabular preview ---------------------------------------------------------

const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

/**
 * Render a list `mapped_output` as a horizontally-scrolling table (columns in output_mapping
 * declaration order). Returns `null` for a non-list result so the panel falls back to the JSON tree.
 */
export const renderTabularMappedOutput = (mappedOutput: any, config: OutputMapperConfig | null): React.ReactNode => {
    if (!Array.isArray(mappedOutput)) return null;
    if (!mappedOutput.every((row) => row && typeof row === 'object' && !Array.isArray(row))) return null;

    return <TabularMappedOutput rows={mappedOutput as Record<string, unknown>[]} config={config} />;
};

const TabularMappedOutput = ({
    rows,
    config,
}: {
    rows: Record<string, unknown>[];
    config: OutputMapperConfig | null;
}) => {
    const { t } = useTranslation('integrations');
    const configuredColumns = config?.output_mapping?.map((mapping) => mapping.target) ?? [];
    const discoveredColumns = rows.flatMap((row) => Object.keys(row));
    const columns = Array.from(new Set([...configuredColumns, ...discoveredColumns]));

    return (
        <div className="flex flex-col h-full border border-line-weak rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-surface-medium text-sm font-medium border-b border-line-weak flex-none">
                {t('outputMapper.form.mappedOutput')}
            </div>
            {columns.length > 0 ? (
                <TableScrollWrapper className="flex-1 min-h-0 border-0 rounded-none">
                    <Table className="min-w-full table-auto text-xs">
                        <Table.Head sticky>
                            <Table.Row>
                                {columns.map((column) => (
                                    <Table.HeadCell key={column} className="text-xs">
                                        {column}
                                    </Table.HeadCell>
                                ))}
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {rows.map((row, rowIndex) => (
                                <Table.Row key={rowIndex}>
                                    {columns.map((column) => (
                                        <Table.Cell key={column} className="h-auto text-xs align-top">
                                            {formatCellValue(row[column])}
                                        </Table.Cell>
                                    ))}
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </TableScrollWrapper>
            ) : (
                <div className="flex-1 min-h-0 p-3 text-sm text-content-weak">
                    {t('outputMapper.form.noMappedOutputRows', 'No mapped output rows')}
                </div>
            )}
        </div>
    );
};

// --- Settings selector -------------------------------------------------------

const SettingRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex items-center gap-2">
        <span className="w-44 shrink-0 font-medium text-content-strong">{label}</span>
        <div className="w-72">{children}</div>
    </div>
);

// The design system's select, as in the document configuration; a setting always has a value, so no clear button.
const Select = <T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (next: T) => void;
}) => (
    <SettingRow label={label}>
        <SelectField
            required
            value={value}
            options={options}
            renderOption={(option) => option.label}
            onValueChange={(next: string) => onChange(next as T)}
        />
    </SettingRow>
);

const TextInput = ({ label, value, onChange }: { label: string; value: string; onChange: (next: string) => void }) => (
    <SettingRow label={label}>
        <StringField value={value} onValueChange={(next) => onChange(next ?? '')} />
    </SettingRow>
);

/**
 * Shared output-settings control for every owner: output_type / iteration_type / default_mapping_source.
 * Selections update the in-memory config so Save persists them. Switching iteration_type on a non-empty
 * mapping asks for confirmation first (cancel reverts).
 */
export const OutputSettingsSelector = ({
    config,
    updateConfig,
    belowMappingSource,
    isFileOutput = true,
}: {
    config: OutputMapperConfig | null;
    updateConfig: (next: ConfigUpdater) => void;
    /** Extra slot rendered below the default-mapping-source select (e.g. the diverged-mapping notice). */
    belowMappingSource?: React.ReactNode;
    /** The output is a delivered file (channels); off for ERP integrations, whose payload goes to an API. */
    isFileOutput?: boolean;
}) => {
    const { t } = useTranslation('integrations');
    const [pendingIteration, setPendingIteration] = useState<IterationType | null>(null);

    if (!config) return null;

    const outputType = getOutputType(config);
    const iterationType = getIterationType(config);
    const mappingSource = getMappingSource(config);
    const xmlSerializerModule = getXmlSerializerModule(config);
    const xmlSerializerModes = config.xml_serializer_modes ?? [];
    const txtDelimiter = getTxtDelimiter(config);
    const templateFormat = getTemplateFormat(config);
    const hasMappings = (config.output_mapping?.length ?? 0) > 0;

    const patch = (changes: Partial<OutputMapperConfig>) =>
        updateConfig((current) => (current ? { ...current, ...changes } : current));

    const applyIteration = (next: IterationType) => patch({ iteration_type: next });

    const requestIterationChange = (next: IterationType) => {
        if (next === iterationType) return;
        if (hasMappings) {
            setPendingIteration(next);
            return;
        }
        applyIteration(next);
    };

    return (
        <div className="flex flex-col gap-3 rounded border border-surface-strong bg-surface-weak p-3 text-sm">
            <div className="flex flex-col gap-3">
                <Select<OutputType>
                    label={t('outputMapper.settings.outputType.label', 'Output type')}
                    value={outputType}
                    onChange={(next) =>
                        patch({
                            output_type: next,
                            ...(usesIteration(next) ? {} : { iteration_type: 'record' as IterationType }),
                        })
                    }
                    options={outputTypeOptions(t)}
                />
                {usesIteration(outputType) && (
                    <Select<IterationType>
                        label={t('outputMapper.settings.iterationType.label', 'Iteration')}
                        value={iterationType}
                        onChange={requestIterationChange}
                        options={[
                            { value: 'record', label: t('outputMapper.settings.iterationType.record', 'Record') },
                            {
                                value: 'line_items',
                                label: t('outputMapper.settings.iterationType.lineItems', 'Line items'),
                            },
                            {
                                value: 'article_suggestions',
                                label: t(
                                    'outputMapper.settings.iterationType.articleSuggestions',
                                    'Article suggestions'
                                ),
                            },
                        ]}
                    />
                )}
                <Select<MappingSource>
                    label={t('outputMapper.settings.mappingSource.label', 'Default mapping source')}
                    value={mappingSource}
                    onChange={(next) => patch({ default_mapping_source: next })}
                    options={[
                        {
                            value: 'channel_fields',
                            label: t('outputMapper.settings.mappingSource.channelFields', 'Channel fields'),
                        },
                        {
                            value: 'integration_default',
                            label: t('outputMapper.settings.mappingSource.integrationDefault', 'Integration default'),
                        },
                    ]}
                />
                {isFileOutput && outputType !== 'xlsx' && (
                    <>
                        <Select<LineEnding | 'standard'>
                            label={t('outputMapper.settings.lineEnding.label', 'Line ending')}
                            value={config.line_ending ?? 'standard'}
                            onChange={(next) => patch({ line_ending: next === 'standard' ? null : next })}
                            options={[
                                {
                                    value: 'standard',
                                    label: t('outputMapper.settings.lineEnding.standard', 'Standard (unchanged)'),
                                },
                                { value: 'lf', label: t('outputMapper.settings.lineEnding.lf', 'LF (Unix)') },
                                {
                                    value: 'crlf',
                                    label: t('outputMapper.settings.lineEnding.crlf', 'CRLF (Windows)'),
                                },
                            ]}
                        />
                        <Select<FileEncoding>
                            label={t('outputMapper.settings.encoding.label', 'Encoding')}
                            value={config.encoding ?? 'utf-8'}
                            onChange={(next) => patch({ encoding: next })}
                            options={[
                                {
                                    value: 'utf-8',
                                    label: t('outputMapper.settings.encoding.utf8', 'UTF-8 (default)'),
                                },
                                {
                                    value: 'utf-8-sig',
                                    label: t('outputMapper.settings.encoding.utf8Bom', 'UTF-8 with BOM (Excel)'),
                                },
                                {
                                    value: 'cp1252',
                                    label: t('outputMapper.settings.encoding.windows1252', 'Windows-1252 (ANSI)'),
                                },
                                {
                                    value: 'iso-8859-1',
                                    label: t('outputMapper.settings.encoding.latin1', 'ISO-8859-1 (Latin-1)'),
                                },
                                {
                                    value: 'iso-8859-15',
                                    label: t('outputMapper.settings.encoding.latin9', 'ISO-8859-15 (Latin-9, with €)'),
                                },
                            ]}
                        />
                    </>
                )}
                {belowMappingSource}
            </div>

            {outputType === 'xml' &&
                (xmlSerializerModes.length > 0 ? (
                    <div className="flex flex-col gap-1">
                        <Select<string>
                            label={t('outputMapper.settings.xmlSerializer.label', 'XML serializer')}
                            value={xmlSerializerModule}
                            onChange={(next) => patch({ xml_serializer_module: next })}
                            options={xmlSerializerModes.map((mode) => ({ value: mode.value, label: mode.label }))}
                        />
                        <p className="text-xs text-content-weak leading-relaxed">
                            {t(
                                'outputMapper.settings.xmlSerializer.hint',
                                'XML is rendered through the selected serializer. The test preview shows the produced XML.'
                            )}
                        </p>
                    </div>
                ) : (
                    <p className="text-xs text-content-weak leading-relaxed">
                        {t(
                            'outputMapper.settings.xmlUnsupportedHint',
                            'XML output is not available for this owner yet.'
                        )}
                    </p>
                ))}
            {outputType === 'txt' && (
                <div className="flex flex-col gap-1">
                    <TextInput
                        label={t('outputMapper.settings.txtDelimiter.label', 'TXT delimiter')}
                        value={txtDelimiter}
                        onChange={(next) => patch({ txt_delimiter: next })}
                    />
                    <p className="text-xs text-content-weak leading-relaxed">
                        {t(
                            'outputMapper.settings.txtDelimiter.hint',
                            'Fields are joined with this delimiter, in the same column order as CSV.'
                        )}
                    </p>
                </div>
            )}
            {outputType === 'xlsx' && (
                <p className="text-xs text-content-weak leading-relaxed">
                    {t(
                        'outputMapper.settings.xlsxHint',
                        'Columns follow the mapping order like CSV. The test preview shows the mapped rows; the exported output file is an Excel workbook (.xlsx).'
                    )}
                </p>
            )}
            {outputType === 'template' && (
                <div className="flex flex-col gap-2">
                    <Select<TemplateFormat>
                        label={t('outputMapper.settings.templateFormat.label', 'Template format')}
                        value={templateFormat}
                        onChange={(next) => patch({ template_format: next })}
                        options={[
                            { value: 'xml', label: t('outputMapper.settings.templateFormat.xml', 'XML') },
                            { value: 'csv', label: t('outputMapper.settings.templateFormat.csv', 'CSV') },
                            { value: 'txt', label: t('outputMapper.settings.templateFormat.txt', 'TXT') },
                        ]}
                    />
                    {templateFormat === 'xml' && (
                        <Select<string>
                            label={t('outputMapper.settings.templateXsd.label', 'XSD validation')}
                            value={config.template_xsd_key ?? ''}
                            onChange={(next) => patch({ template_xsd_key: next || null })}
                            options={[
                                { value: '', label: t('outputMapper.settings.templateXsd.none', 'None') },
                                ...(config.template_xsds ?? []).map((xsd) => ({ value: xsd.key, label: xsd.label })),
                            ]}
                        />
                    )}
                    {templateFormat === 'xml' && config.template_xsd_key === 'custom' && (
                        <div className="flex flex-col gap-1">
                            <span className="font-medium text-content-strong">
                                {t('outputMapper.settings.templateXsd.customLabel', 'Custom XSD content')}
                            </span>
                            <div className={classnames(FIELD_FRAME_CLASS, 'max-h-[70vh] overflow-auto')}>
                                <Editor
                                    className="text-xs font-mono"
                                    style={{ minHeight: '40vh' }}
                                    value={config.template_xsd ?? ''}
                                    onValueChange={(next: string) => patch({ template_xsd: next || null })}
                                    highlight={(code: string) => highlight(code, languages.markup)}
                                    padding={8}
                                    placeholder={t(
                                        'outputMapper.settings.templateXsd.customPlaceholder',
                                        'Paste the customer XSD here (self-contained — xsd:import/include of other files is not resolved; max 1 MB).'
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <p className="text-xs text-content-weak leading-relaxed">
                        {t(
                            'outputMapper.settings.templateHint',
                            'Output is rendered through the Jinja template authored in the Template tab; target fields are available as variables and missing values render empty. XML values are escaped automatically; in CSV templates quote text cells with the csv filter. With an XSD selected, test runs and the production output are validated against that schema.'
                        )}
                    </p>
                </div>
            )}
            {iterationType === 'article_suggestions' && (
                <p className="text-xs text-content-weak leading-relaxed">
                    {t(
                        'outputMapper.settings.articleSuggestionsHint',
                        'Each FieldMapping becomes a column; bind sources as `Article.<field>`, `ArticleSet.<field>`, `Position.<field>`, or `Header.<field>`.'
                    )}
                </p>
            )}

            {pendingIteration && (
                <ConfirmModal
                    visible
                    title={t('outputMapper.settings.confirmIterationTitle', 'Change iteration?')}
                    labelConfirm={t('outputMapper.settings.confirmIterationAction', 'Change iteration')}
                    onCancel={() => setPendingIteration(null)}
                    onConfirm={() => {
                        applyIteration(pendingIteration);
                        setPendingIteration(null);
                    }}
                >
                    <div className="text-sm text-content-strong">
                        {t(
                            'outputMapper.settings.confirmIterationBody',
                            'Changing the iteration reinterprets all field bindings. Record fields run once per document; line-item and article fields run once per item and bind the current item as an additional input root.'
                        )}
                    </div>
                </ConfirmModal>
            )}
        </div>
    );
};
